import { createSlice } from '@reduxjs/toolkit';
import {
  fetchStatus,
  register,
  fetchCommission,
  fetchPromotedProduct,
  updateAffiliate,
  recordAttribution,
  hydrateAttribution,
  isDuplicateNikError,
  SEVEN_DAYS_MS,
  getAffiliateInfo,
  getAffiliatePromotedProducts,
  getAffiliateAllPromotedProducts,
  getAffiliatePromotedProductsCount,
  getOrderSocoLinkList,
  getBenefitSocoLinkList,
  getSocoLinkHelpPosts,
  searchPromotedProducts,
  getPromotedProductsSearchCount,
  getPromoteMoreProducts,
  getPromoteMoreProductsCount,
} from '~store/affiliate/thunks';

// status values returned by ms-affiliates that mean the user IS a SOCOLink.
const REGISTERED_STATUSES = ['active', 'blacklisted', 'inactive'];

const DUPLICATE_NIK_MESSAGE = 'NIK sudah terdaftar, mohon gunakan NIK lain.';
const GENERIC_ERROR_MESSAGE = 'Maaf, terjadi kendala. Mohon coba kembali.';

// Shared error mapping for register / KTP-update rejections.
const mapAffiliateError = (payload) =>
  isDuplicateNikError(payload)
    ? { field: 'nik', message: DUPLICATE_NIK_MESSAGE }
    : { field: null, message: payload?.message || GENERIC_ERROR_MESSAGE };

const status = {
  fetch: 'idle',
  register: 'idle',
  update: 'idle',
};

export const { actions, reducer } = createSlice({
  name: 'affiliate',
  initialState: {
    info: null,
    selectedAddress: {},
    registerError: null,
    commission: {},
    commissionStatus: {},
    promotedIds: {},
    attribution: {},
    status,

    // --- SOCOLink dashboard ---
    affiliateInfo: null,
    affiliateInfoStatus: 'idle',
    promotedProducts: [],
    promotedProductsStatus: 'idle',
    allPromotedProducts: [],
    allPromotedProductsStatus: 'idle',
    allPromotedProductsLoadMoreStatus: 'idle',
    promotedProductsCount: 0,
    orderList: [],
    orderListStatus: 'idle',
    orderListLoadMoreStatus: 'idle',
    orderListHasMore: true,
    benefitList: [],
    benefitListStatus: 'idle',
    benefitListLoadMoreStatus: 'idle',
    benefitListHasMore: true,
    helpPosts: [],
    helpPostsStatus: 'idle',
    searchedPromotedProducts: [],
    searchedPromotedProductsStatus: 'idle',
    searchedPromotedProductsLoadMoreStatus: 'idle',
    searchedPromotedProductsCount: 0,
    promoteMoreProducts: [],
    promoteMoreProductsStatus: 'idle',
    promoteMoreProductsLoadMoreStatus: 'idle',
    promoteMoreProductsCount: 0,
  },
  reducers: {
    setSelectedAddress: (state, { payload }) => {
      state.selectedAddress = payload || {};
    },
    resetSelectedAddress: (state) => {
      state.selectedAddress = {};
    },
    clearRegisterError: (state) => {
      state.registerError = null;
    },
    resetUpdate: (state) => {
      state.status.update = 'idle';
      state.registerError = null;
    },
    resetPromotedProductsSearch: (state) => {
      state.searchedPromotedProducts = [];
      state.searchedPromotedProductsStatus = 'idle';
      state.searchedPromotedProductsLoadMoreStatus = 'idle';
      state.searchedPromotedProductsCount = 0;
    },
    setWebAttribution: (state, { payload }) => {
      const { productId, affiliateProductId, affiliateUserId } = payload;
      const pid = String(productId || '');
      const apid = Number(affiliateProductId) || 0;
      if (!pid || !apid) {
        return;
      }
      state.attribution[pid] = {
        product_id: pid,
        affiliate_product_id: apid,
        affiliate_user_id: affiliateUserId ? String(affiliateUserId) : null,
        click_date: Date.now(),
      };
    },
    clearWebAttribution: (state, { payload: productId }) => {
      if (productId) {
        delete state.attribution[String(productId)];
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // --- status & summary ---
      .addCase(fetchStatus.pending, (state) => {
        state.status.fetch = 'pending';
      })
      .addCase(fetchStatus.fulfilled, (state, { payload }) => {
        state.info = payload || null;
        state.status.fetch = 'succeeded';
      })
      .addCase(fetchStatus.rejected, (state) => {
        // Fail silently — the Account page must keep working.
        state.info = null;
        state.status.fetch = 'failed';
      })

      // --- registration ---
      .addCase(register.pending, (state) => {
        state.status.register = 'pending';
        state.registerError = null;
      })
      .addCase(register.fulfilled, (state, { payload }) => {
        state.info = payload || state.info;
        state.status.register = 'succeeded';
      })
      .addCase(register.rejected, (state, { payload }) => {
        state.status.register = 'failed';
        state.registerError = mapAffiliateError(payload);
      })

      // --- PDP/PCP commission & blacklist ---
      .addCase(fetchCommission.pending, (state, { meta }) => {
        state.commissionStatus[meta.arg.id] = 'pending';
      })
      .addCase(fetchCommission.fulfilled, (state, { payload }) => {
        state.commission[payload.id] = payload.data;
        state.commissionStatus[payload.id] = 'succeeded';
      })
      .addCase(fetchCommission.rejected, (state, { payload, meta }) => {
        const id = payload?.id || meta.arg.id;
        state.commissionStatus[id] = 'failed';
      })

      // --- promoted product (affiliate share link id) ---
      .addCase(fetchPromotedProduct.fulfilled, (state, { payload }) => {
        if (payload?.affiliates_product_id) {
          state.promotedIds[payload.productId] = payload.affiliates_product_id;
        }
      })

      // --- affiliate attribution cache (7-day) ---
      .addCase(recordAttribution.fulfilled, (state, { payload }) => {
        if (payload) {
          state.attribution = payload;
        }
      })
      .addCase(hydrateAttribution.fulfilled, (state, { payload }) => {
        state.attribution = payload || {};
      })

      // --- update / re-submit KTP ---
      .addCase(updateAffiliate.pending, (state) => {
        state.status.update = 'pending';
        state.registerError = null;
      })
      .addCase(updateAffiliate.fulfilled, (state, { payload }) => {
        state.info = payload || state.info;
        state.status.update = 'succeeded';
      })
      .addCase(updateAffiliate.rejected, (state, { payload }) => {
        state.status.update = 'failed';
        state.registerError = mapAffiliateError(payload);
      })

      // --- SOCOLink dashboard ---
      .addCase(getAffiliateInfo.pending, (state) => {
        state.affiliateInfoStatus = 'pending';
      })
      .addCase(getAffiliateInfo.rejected, (state) => {
        state.affiliateInfoStatus = 'failed';
      })
      .addCase(getAffiliateInfo.fulfilled, (state, { payload }) => {
        state.affiliateInfo = payload;
        state.affiliateInfoStatus = 'succeeded';
      })
      .addCase(getAffiliatePromotedProducts.pending, (state) => {
        state.promotedProductsStatus = 'pending';
      })
      .addCase(getAffiliatePromotedProducts.rejected, (state) => {
        state.promotedProductsStatus = 'failed';
      })
      .addCase(getAffiliatePromotedProducts.fulfilled, (state, { payload }) => {
        state.promotedProducts = payload || [];
        state.promotedProductsStatus = 'succeeded';
      })
      .addCase(getAffiliateAllPromotedProducts.pending, (state, { meta }) => {
        if (meta.arg.fetchMore) {
          state.allPromotedProductsLoadMoreStatus = 'pending';
        } else {
          state.allPromotedProductsStatus = 'pending';
        }
      })
      .addCase(getAffiliateAllPromotedProducts.rejected, (state, { meta }) => {
        if (meta.arg.fetchMore) {
          state.allPromotedProductsLoadMoreStatus = 'failed';
        } else {
          state.allPromotedProductsStatus = 'failed';
        }
      })
      .addCase(
        getAffiliateAllPromotedProducts.fulfilled,
        (state, { payload }) => {
          if (payload.fetchMore) {
            state.allPromotedProducts = [
              ...state.allPromotedProducts,
              ...payload.data,
            ];
            state.allPromotedProductsLoadMoreStatus = 'succeeded';
          } else {
            state.allPromotedProducts = payload.data;
            state.allPromotedProductsStatus = 'succeeded';
          }
        },
      )
      .addCase(
        getAffiliatePromotedProductsCount.fulfilled,
        (state, { payload }) => {
          state.promotedProductsCount = payload || 0;
        },
      )
      .addCase(getOrderSocoLinkList.pending, (state, { meta }) => {
        if (meta.arg.fetchMore) {
          state.orderListLoadMoreStatus = 'pending';
        } else {
          state.orderListStatus = 'pending';
          state.orderListHasMore = true;
        }
      })
      .addCase(getOrderSocoLinkList.rejected, (state, { meta }) => {
        if (meta.arg.fetchMore) {
          state.orderListLoadMoreStatus = 'failed';
        } else {
          state.orderListStatus = 'failed';
        }
      })
      .addCase(getOrderSocoLinkList.fulfilled, (state, { payload }) => {
        if (payload.fetchMore) {
          state.orderList = [...state.orderList, ...payload.data];
          state.orderListLoadMoreStatus = 'succeeded';
        } else {
          state.orderList = payload.data;
          state.orderListStatus = 'succeeded';
        }
        state.orderListHasMore = payload.hasMore;
      })
      .addCase(getBenefitSocoLinkList.pending, (state, { meta }) => {
        if (meta.arg.fetchMore) {
          state.benefitListLoadMoreStatus = 'pending';
        } else {
          state.benefitListStatus = 'pending';
          state.benefitListHasMore = true;
        }
      })
      .addCase(getBenefitSocoLinkList.rejected, (state, { meta }) => {
        if (meta.arg.fetchMore) {
          state.benefitListLoadMoreStatus = 'failed';
        } else {
          state.benefitListStatus = 'failed';
        }
      })
      .addCase(getBenefitSocoLinkList.fulfilled, (state, { payload }) => {
        if (payload.fetchMore) {
          state.benefitList = [...state.benefitList, ...payload.data];
          state.benefitListLoadMoreStatus = 'succeeded';
        } else {
          state.benefitList = payload.data;
          state.benefitListStatus = 'succeeded';
        }
        state.benefitListHasMore = payload.hasMore;
      })
      .addCase(getSocoLinkHelpPosts.pending, (state) => {
        state.helpPostsStatus = 'pending';
      })
      .addCase(getSocoLinkHelpPosts.rejected, (state) => {
        state.helpPostsStatus = 'failed';
      })
      .addCase(getSocoLinkHelpPosts.fulfilled, (state, { payload }) => {
        state.helpPosts = payload || [];
        state.helpPostsStatus = 'succeeded';
      })
      .addCase(searchPromotedProducts.pending, (state, { meta }) => {
        if (meta.arg.fetchMore) {
          state.searchedPromotedProductsLoadMoreStatus = 'pending';
        } else {
          state.searchedPromotedProductsStatus = 'pending';
        }
      })
      .addCase(searchPromotedProducts.rejected, (state, { meta }) => {
        if (meta.arg.fetchMore) {
          state.searchedPromotedProductsLoadMoreStatus = 'failed';
        } else {
          state.searchedPromotedProductsStatus = 'failed';
        }
      })
      .addCase(searchPromotedProducts.fulfilled, (state, { payload }) => {
        if (payload.fetchMore) {
          state.searchedPromotedProducts = [
            ...state.searchedPromotedProducts,
            ...payload.data,
          ];
          state.searchedPromotedProductsLoadMoreStatus = 'succeeded';
        } else {
          state.searchedPromotedProducts = payload.data;
          state.searchedPromotedProductsStatus = 'succeeded';
        }
      })
      .addCase(
        getPromotedProductsSearchCount.fulfilled,
        (state, { payload }) => {
          state.searchedPromotedProductsCount = payload || 0;
        },
      )
      .addCase(getPromoteMoreProducts.pending, (state, { meta }) => {
        if (meta.arg.fetchMore) {
          state.promoteMoreProductsLoadMoreStatus = 'pending';
        } else {
          state.promoteMoreProductsStatus = 'pending';
        }
      })
      .addCase(getPromoteMoreProducts.rejected, (state, { meta }) => {
        if (meta.arg.fetchMore) {
          state.promoteMoreProductsLoadMoreStatus = 'failed';
        } else {
          state.promoteMoreProductsStatus = 'failed';
        }
      })
      .addCase(getPromoteMoreProducts.fulfilled, (state, { payload }) => {
        if (payload.fetchMore) {
          state.promoteMoreProducts = [
            ...state.promoteMoreProducts,
            ...payload.data,
          ];
          state.promoteMoreProductsLoadMoreStatus = 'succeeded';
        } else {
          state.promoteMoreProducts = payload.data;
          state.promoteMoreProductsStatus = 'succeeded';
        }
      })
      .addCase(getPromoteMoreProductsCount.fulfilled, (state, { payload }) => {
        state.promoteMoreProductsCount = payload || 0;
      });
  },
});

export const selectIsRegistered = (state) =>
  REGISTERED_STATUSES.includes(state.affiliate.info?.status);

export const selectIsActiveAffiliate = (state) =>
  state.affiliate.info?.status === 'active';

export const selectValidAttribution = (state, productId) => {
  const entry = state.affiliate.attribution?.[String(productId)];
  if (!entry) {
    return null;
  }
  if (Date.now() - (entry.click_date || 0) >= SEVEN_DAYS_MS) {
    return null;
  }
  return entry;
};

export default reducer;
